/**
 * Color utilities for converting HEX colors to HSL format
 * used by CSS custom properties (e.g. "217 91% 50%").
 */

export interface HSL {
  h: number;
  s: number;
  l: number;
}

/**
 * Convert a HEX color string to HSL components.
 * Returns null if the input is not a valid 6-digit hex color.
 */
export function hexToHsl(hex: string): HSL | null {
  const match = hex.match(/^#([0-9a-fA-F]{6})$/);
  if (!match) return null;

  const r = parseInt(match[1].substring(0, 2), 16) / 255;
  const g = parseInt(match[1].substring(2, 4), 16) / 255;
  const b = parseInt(match[1].substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: Math.round(l * 100) };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Format HSL object to CSS custom property value: "217 91% 50%"
 */
export function hslToCssValue(hsl: HSL): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

/**
 * Compute relative luminance from a HEX color (WCAG formula).
 * Returns a value between 0 (black) and 1 (white).
 */
export function getRelativeLuminance(hex: string): number {
  const match = hex.match(/^#([0-9a-fA-F]{6})$/);
  if (!match) return 0;

  const linearize = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };

  const r = linearize(parseInt(match[1].substring(0, 2), 16));
  const g = linearize(parseInt(match[1].substring(2, 4), 16));
  const b = linearize(parseInt(match[1].substring(4, 6), 16));

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Get a suitable foreground color (as CSS HSL value) for the given background hex.
 * Returns white for dark backgrounds, near-black for light backgrounds.
 */
export function getContrastForeground(hex: string): string {
  const luminance = getRelativeLuminance(hex);
  // White foreground for dark colors, dark foreground for light colors
  return luminance > 0.4 ? "220 20% 10%" : "0 0% 100%";
}

/**
 * Create a slightly adjusted variant of a color for dark mode.
 * Increases lightness and slightly reduces saturation.
 */
export function adjustForDarkMode(hsl: HSL): HSL {
  return {
    h: hsl.h,
    s: Math.max(hsl.s - 5, 0),
    l: Math.min(hsl.l + 10, 70),
  };
}

/**
 * Create accent-foreground: a darker/lighter version of the color itself.
 */
export function getAccentForeground(hsl: HSL): string {
  // For the accent, the foreground should be a darker version of the hue
  return `${hsl.h} ${Math.min(hsl.s, 91)}% ${Math.max(hsl.l - 10, 30)}%`;
}

/**
 * Create the accent background: a very light tint of the color.
 */
export function getAccentBackground(hsl: HSL): string {
  return `${hsl.h} ${Math.min(hsl.s, 91)}% 95%`;
}

/**
 * Create the dark-mode accent background: a very dark tint of the color.
 */
export function getAccentBackgroundDark(hsl: HSL): string {
  return `${hsl.h} ${Math.min(hsl.s, 91)}% 20%`;
}

/**
 * Create the dark-mode accent foreground: a lighter version.
 */
export function getAccentForegroundDark(hsl: HSL): string {
  return `${hsl.h} ${Math.min(hsl.s, 91)}% 70%`;
}

/**
 * RGB color interface for jsPDF.
 */
export interface RGB {
  r: number;
  g: number;
  b: number;
}

/**
 * Convert a HEX color string to RGB components (0-255).
 * Returns null if the input is not a valid 6-digit hex color.
 */
export function hexToRgb(hex: string | undefined | null): RGB | null {
  if (!hex) return null;
  const match = hex.match(/^#([0-9a-fA-F]{6})$/);
  if (!match) return null;

  return {
    r: parseInt(match[1].substring(0, 2), 16),
    g: parseInt(match[1].substring(2, 4), 16),
    b: parseInt(match[1].substring(4, 6), 16),
  };
}

/**
 * Create a darker variant of an RGB color (multiply each channel).
 */
export function darkenRgb(rgb: RGB, factor: number = 0.8): RGB {
  return {
    r: Math.round(rgb.r * factor),
    g: Math.round(rgb.g * factor),
    b: Math.round(rgb.b * factor),
  };
}

/**
 * Create a lighter variant of an RGB color (mix with white).
 */
export function lightenRgb(rgb: RGB, factor: number = 0.3): RGB {
  return {
    r: Math.round(rgb.r + (255 - rgb.r) * factor),
    g: Math.round(rgb.g + (255 - rgb.g) * factor),
    b: Math.round(rgb.b + (255 - rgb.b) * factor),
  };
}
